/**
 * DynamicForm — renders an ExposedWorkflow as a multi-step Mantine form.
 *
 * Maps FormStep[] → Mantine Stepper, FormField[] → DynamicFormField inputs.
 * Handles conditional field visibility (conditionalFieldId/conditionalEquals),
 * client-side validation via PortalFormService, and submission.
 */
import { useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Group,
  Stack,
  Stepper,
  Text,
  Title,
} from '@mantine/core';
import { IconCheck, IconAlertCircle, IconSend, IconArrowLeft, IconArrowRight } from '@tabler/icons-react';
import type { ExposedWorkflow, FormField, FormStep } from '../../data/portal/types';
import type { ValidationError } from '../../data/portal/services/PortalFormService';
import { DynamicFormField } from './DynamicFormField';

interface DynamicFormProps {
  workflow: ExposedWorkflow;
  onSubmit: (data: Record<string, unknown>) => Promise<void>;
  isSubmitting: boolean;
  validationErrors: ValidationError[];
  error: Error | null;
  submitted: boolean;
}

export function DynamicForm({
  workflow,
  onSubmit,
  isSubmitting,
  validationErrors,
  error,
  submitted,
}: DynamicFormProps) {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const steps = workflow.steps;
  const isMultiStep = steps.length > 1;

  const handleFieldChange = useCallback((fieldId: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  }, []);

  const isFieldVisible = useCallback(
    (field: FormField): boolean => {
      if (!field.conditionalFieldId) return true;
      const depValue = formData[field.conditionalFieldId];
      return String(depValue ?? '') === (field.conditionalEquals ?? '');
    },
    [formData],
  );

  const getFieldError = useCallback(
    (fieldId: string): string | undefined => {
      return validationErrors.find((e) => e.fieldId === fieldId)?.message;
    },
    [validationErrors],
  );

  const handleSubmit = useCallback(async () => {
    await onSubmit(formData);
  }, [formData, onSubmit]);

  if (submitted) {
    return (
      <Alert icon={<IconCheck size={16} />} title="Submitted" color="green" variant="light">
        {workflow.confirmationContent || 'Your form has been submitted successfully.'}
      </Alert>
    );
  }

  const renderStepFields = (step: FormStep) => (
    <Stack gap="md">
      {step.description && (
        <Text c="dimmed" size="sm">{step.description}</Text>
      )}
      {step.fields.filter(isFieldVisible).map((field) => (
        <DynamicFormField
          key={field.id}
          field={field}
          value={formData[field.id]}
          onChange={handleFieldChange}
          error={getFieldError(field.id)}
        />
      ))}
    </Stack>
  );

  return (
    <Box>
      <Title order={3} mb="lg">{workflow.displayName}</Title>

      {error && (
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" variant="light" mb="md">
          {error.message}
        </Alert>
      )}

      {isMultiStep ? (
        <Stepper active={activeStep} onStepClick={setActiveStep}>
          {steps.map((step, index) => (
            <Stepper.Step key={index} label={step.title ?? `Step ${index + 1}`}>
              {renderStepFields(step)}
            </Stepper.Step>
          ))}
          <Stepper.Completed>
            <Alert icon={<IconCheck size={16} />} color="green" variant="light">
              Review your information and submit.
            </Alert>
          </Stepper.Completed>
        </Stepper>
      ) : (
        steps[0] && renderStepFields(steps[0])
      )}

      <Group justify="flex-end" mt="xl">
        {isMultiStep && activeStep > 0 && (
          <Button
            variant="default"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => setActiveStep((s) => s - 1)}
          >
            Back
          </Button>
        )}
        {isMultiStep && activeStep < steps.length - 1 ? (
          <Button
            rightSection={<IconArrowRight size={16} />}
            onClick={() => setActiveStep((s) => s + 1)}
          >
            Next
          </Button>
        ) : (
          <Button
            leftSection={<IconSend size={16} />}
            loading={isSubmitting}
            onClick={handleSubmit}
          >
            Submit
          </Button>
        )}
      </Group>
    </Box>
  );
}
